import TableContainer from '@mui/material/TableContainer';
import Typography from '@mui/material/Typography';
import { ELEVATION } from '@openx/components-core/lib/theme/elevation';
import { Table, TableName } from '@openx/components-table/Table';
import type { LalSegmentApi, LookalikeSegmentSimilarityApi } from '@openx/types/segments';
import type { TableColumn } from '@openx/types/tableHelpers';
import { abbreviatedNumber } from '@openx/utils/lib/abbreviatedNumber';
import { useMemo } from 'react';
import { sortByPriority } from '../../../utils/sortByPriority';
import { NameCell } from './parts/NameCell';
import { ReachType } from './parts/ReachType';

type Props = {
  seedAggregate: LalSegmentApi['lookalike_segment_seed_aggregate'];
  segmentSimilarities: LookalikeSegmentSimilarityApi[];
};

export const LalSegmentReachTable = ({ seedAggregate, segmentSimilarities }: Props) => {
  const seedSegment = seedAggregate?.segment;
  const sortedSegmentSimilarities = useMemo(() => sortByPriority(segmentSimilarities), [segmentSimilarities]);

  const columns: TableColumn<LookalikeSegmentSimilarityApi>[] = useMemo(
    () => [
      {
        key: 'type',
        title: 'Type',
        width: '20%',
        headerStyle: { display: 'none' },
        render: ({ similarity_level }) => <ReachType type={similarity_level} />,
      },
      {
        key: 'name',
        title: 'Name',
        width: '65%',
        headerStyle: { display: 'none' },
        render: ({ segment, similarity_level }) =>
          segment.reach && seedSegment?.reach ? (
            <NameCell segment={segment} seed={seedSegment} type={similarity_level} />
          ) : null,
      },
      {
        key: 'reach',
        title: 'Reach',
        width: '15%',
        headerStyle: { display: 'none' },
        align: 'right',
        render: ({ segment, similarity_level }) =>
          segment.reach ? (
            <Typography
              component="span"
              sx={{
                fontWeight: 'fontWeightBold',
                fontSize: 16,
              }}
            >
              {similarity_level === 'Seed' ? '' : '+'}
              {abbreviatedNumber(segment.reach)}
            </Typography>
          ) : null,
      },
    ],
    [seedSegment]
  );

  if (!sortedSegmentSimilarities?.length || !seedSegment) {
    return null;
  }

  const data = [
    {
      id: seedSegment.id,
      segment: seedSegment,
      similarity_level: 'Seed',
    },
    ...sortedSegmentSimilarities,
  ];

  return (
    <TableContainer sx={theme => ({ boxShadow: ELEVATION.elevation0, border: 1, borderColor: theme.palette.divider })}>
      <Table
        data-test="lal-segment-reach-table"
        enableTableHeaderCustomization={false}
        name={TableName.LAL_SEGMENT_REACH}
        data={data}
        columns={columns}
        loading={false}
        overrideTableCellStyles={{
          minHeight: '60px',
          '&:first-of-type': {
            paddingLeft: '12px',
          },
          '&:last-child:not(.iconCell, .expandContentCell)': {
            paddingRight: '12px',
          },
        }}
      />
    </TableContainer>
  );
};
